<template>
    <b-modal
      id="add-or-update"
      :visible="isAddOrUpdateActive"
      centered
      hide-footer
      hide-header
      no-close-on-esc
      no-close-on-backdrop
      @hidden="resetForm" 
      @hide="resetForm"
      @change="(val) => $emit('update:is-add-or-update-active', val)"
    >
      <template #default="{ hide }">
        <b-overlay :show="loading" class="px-2">
          <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
            <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
              {{ isUpdate ? 'Update Area' : 'Add New Area' }}
            </h5>
  
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
  
          <validation-observer
            #default="{ handleSubmit }"
            ref="update_modal_form"
          >
            <b-form
              class="my-1"
              @submit.prevent="handleSubmit(onCreateOrUpdate)"
              @reset.prevent="resetForm"
            >
  
              <b-alert
                v-if="errorMessage"
                variant="danger"
                :show="true"
              >
                <div class="alert-body font-small-2">
                  <b-card-text class="mb-0">
                    {{ errorMessage }}
                  </b-card-text>
                </div>
              </b-alert>
  
        
              
              <validation-provider
                #default="validationContext"
                name="Title"
                rules="required"
              >
                <b-form-group
                  label="Title"
                  label-for="title"
                >
                  <template #label>
                    <span>Title<sup style="color: tomato">*</sup></span>
                  </template>
  
                  <b-form-input
                    id="title"
                    v-model="new_data.title"
                    autofocus
                    placeholder=""
                    :disabled="disableForm"
                    :state="getValidationState(validationContext)"
                  />
  
                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
  
  
              <!-- Parent type -->
              <b-form-group label="Belongs To" label-for="parent_type" class="mb-2">
                <b-form-radio-group
                  id="parent_type"
                  v-model="parentType"
                  :disabled="isUpdate || disableForm"
                  :options="[
                    { text: 'A Region (Ghana)', value: 'region' },
                    { text: 'A Country (Missions)', value: 'country' },
                  ]"
                />
              </b-form-group>

              <!-- Region -->
              <b-form-group v-if="parentType === 'region'" label="Region" label-for="Region">
                <template #label>
                  <span>Region<span style="color: tomato">*</span></span>
                </template>

                <validation-provider
                  #default="{ errors }"
                  name="Region"
                  :rules="parentType === 'region' ? 'required' : ''"
                >
                  <v-select
                    id="Region"
                    v-model="new_data.region_id"
                    :loading="regionsLoading"
                    :clearable="false"
                    :disabled="disableForm"
                    :options="regionOptions"
                    :reduce="(val) => val.value"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Country -->
              <b-form-group v-else label="Country" label-for="Country">
                <template #label>
                  <span>Country<span style="color: tomato">*</span></span>
                </template>

                <validation-provider
                  #default="{ errors }"
                  name="Country"
                  :rules="parentType === 'country' ? 'required' : ''"
                >
                  <v-select
                    id="Country"
                    v-model="new_data.country_id"
                    :loading="countriesLoading"
                    :clearable="false"
                    :disabled="disableForm"
                    :options="countryOptions"
                    :reduce="(val) => val.value"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- RMC (read-only, derived from selected Country) -->
              <b-form-group v-if="parentType === 'country' && new_data.country_id" label="RMC" label-for="rmc_display">
                <b-form-input
                  id="rmc_display"
                  :value="selectedCountryRmc || 'No RMC Assigned'"
                  disabled
                />
              </b-form-group>

              <!-- mission zone -->
              <b-form-group label="Mission Zone?" label-for="mission_zone" class="mb-2">
                <validation-provider
                  #default="{ errors }"
                  name="mission_zone"
                  rules=""
                >
                  <b-form-checkbox
                    id="mission_zone"
                    v-model="new_data.mission_zone"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
    
              <!-- Form Actions -->
              <div class="d-flex mt-2">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mr-2"
                  type="submit"
                  :disabled="disableForm"
                >
                  {{ isUpdate ? 'Update' : 'Add' }}
                </b-button>
  
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  type="button"
                  variant="outline-secondary"
                  @click="hide"
                >
                  Cancel
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </b-overlay>
      </template>
    </b-modal>
  </template>
  
  <script>
  import {
    BOverlay, BSidebar, BForm, BFormGroup, BFormInput,
    BFormTextarea, BFormInvalidFeedback, BButton, BFormFile, BFormCheckbox,
    BFormRadioGroup, BAlert, BCardText, BModal, BMedia, BMediaBody, BMediaAside, BImg,
  } from 'bootstrap-vue'

  import { required } from '@validations'
  import { get, cloneDeep, omit } from 'lodash';
  import { ValidationProvider, ValidationObserver } from 'vee-validate'

  import vSelect from 'vue-select'
  import Ripple from 'vue-ripple-directive'
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

  const rawMemberTypesData = {
    title: "",
    region_id: "",
    country_id: "",
    mission_zone: false,
  }

  export default {
    components: {
      BImg,
      BForm,
      BAlert,
      BModal,
      BMedia,
      BButton,
      vSelect,
      BOverlay,
      BSidebar,
      BCardText,
      BFormFile,
      BFormGroup,
      BMediaBody,
      BFormInput,
      BMediaAside,
      BFormCheckbox,
      BFormTextarea,
      BFormRadioGroup,
      BFormInvalidFeedback,

      // Form Validation
      ValidationProvider,
      ValidationObserver,
    },
    directives: {
      Ripple,
    },
    model: {
      prop: 'isAddOrUpdateActive',
      event: 'update:is-add-or-update-active',
    },
    props: {
      isAddOrUpdateActive: {
        type: Boolean,
        required: true,
      },
      updateData: {
        type: Object,
        default: null,
        required: false
      },

    },
    data() {
      return {
        new_data: cloneDeep(rawMemberTypesData),
        loading: false,
        errorMessage: "",
        required,
        parentType: 'region',
        regionOptions: [],
        regionsLoading: false,
        countryOptions: [],
        countriesLoading: false,
      }
    },
    computed: {
      isUpdate(){
        return !!this.updateData
      },
      disableForm(){
        if (this.isUpdate){
          return !this.can("areas:CanUpdate")
        }

        return !this.can("areas:CanAddNew")
      },
      selectedCountryRmc(){
        const selected = this.countryOptions.find((option) => option.value === this.new_data.country_id)
        return get(selected, 'rmc', null)
      },
    },
    watch: {
      updateData: {
        handler(updateData){
          if (updateData){
            this.parentType = updateData.country_id ? 'country' : 'region';
            this.new_data = {
              _id: updateData._id,
              title: updateData.title,
              mission_zone: updateData.mission_zone,
              region_id: updateData.region_id || "",
              country_id: updateData.country_id || "",
            }
          }
        },
        immediate: true,
        deep: true
      },
    },
    mounted() {
      this.fetchGeoRegions()
      this.fetchCountries()
    },
    methods: {
      async onCreateOrUpdate(){
        try {
          this.loading = true;
          this.errorMessage = "";
  
          const success = await this.$refs.update_modal_form.validate();
          if (!success){
            return;
          }
  
          const messages = {
            update: {
              title: "Confirm Area Update",
              text: "You are about to update details of a area.",
              icon: "warning"
            },
            new: {
              title: "Create Area",
              text: "You are about to create an area",
              icon: "info"
            }
          }
  
          /* eslint-disable no-nested-ternary */
          const message = this.isUpdate ? messages.update : messages.new;
          const { title, text, icon } = message;
  
          const result = await this.$swal({
            title,
            text,
            icon,
            showCancelButton: true,
            confirmButtonText: 'Yes, proceed.',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          });
          if (!result.isConfirmed) {
            return;
          }
  
          const payload = this.parentType === 'region'
            ? omit(this.new_data, "country_id")
            : omit(this.new_data, "region_id");

          if (this.isUpdate){
            const id = payload._id
            const update_data = omit(payload, "_id");
            await this.useJwt().sharedService.updateGeoArea(id, update_data);
          } else {
            await this.useJwt().sharedService.createGeoArea(payload);
          }
          this.$emit("refetchData");
          this.$emit('update:is-add-or-update-active', false);
  
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success!',
              icon: 'AlertQuestionIcon',
              variant: 'success',
              text: `Member ${this.isUpdate ? 'updated' : 'added'} successfully`
            },
          });
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          this.errorMessage = error_message;
  
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.loading = false
        }
      },
      async fetchGeoRegions() {
        try {
          this.regionsLoading = true;

          const request = await this.useJwt().sharedService.fetchGeoRegions();
          const { data } = request.data;

          this.regionOptions = data.map((item) => ({
            label: item.title,
            value: item._id
          }))
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          console.log("error_message", error_message);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.regionsLoading = false;
        }
      },
      async fetchCountries() {
        try {
          this.countriesLoading = true;

          const request = await this.useJwt().sharedService.fetchCountries();
          const { data } = request.data;

          this.countryOptions = (data || []).map((item) => ({
            label: item.country,
            value: item._id,
            rmc: get(item, 'rmc.title', null),
          }))
        } catch (error) {
          const error_message = get(error, "response.data.message") || error.message
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        } finally {
          this.countriesLoading = false;
        }
      },

      resetForm() {
        this.$emit("resetUpdateData")
        this.parentType = 'region';
        this.new_data = cloneDeep(rawMemberTypesData);
      }
    }
  }
  </script>
  
  <style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
  
  #add-or-update {
    .vs__dropdown-menu {
      max-height: 200px !important;
    }
  }
  </style>
